/**
 * PageHeaderBackground Component
 *
 * A page header with image/color background and fixed height per breakpoint.
 * Used for main pages with visual impact.
 *
 * Features:
 * - Fixed height per breakpoint (configurable via theme)
 * - Background image with filters: grayscale, opacity, color overlay
 * - Supports go back link, title, and description
 * - Text color configurable via theme variable
 *
 * @module components/Molecules/PageHeaderBackground
 * @see https://gitlab.earth.bsc.es/ess/essvoltocomponents/-/issues/14
 * @see https://www.figma.com/design/6dZKp0OgtarsESIk3jLBa2/ESS-Design-System-Kelsey?node-id=2281-26704
 */
import React, { useState, useEffect } from 'react';
import { useHistory } from 'react-router-dom';
import cx from 'classnames';
import {
  ContentNarrow, 
  ContentWide,
  ContentWrapper,
} from 'volto-essvoltocomponents/components/Layout';
import PageSection from '../../Layout/PageSection';
import { RichText } from 'volto-essvoltocomponents/components/Atoms';
import { ESSButton } from '../../Atoms/ESSButton';
import { handleBackClick} from 'volto-essvoltocomponents/helpers/urlUtils';
import './PageHeaderBackground.scss';

type Alignment = 'left' | 'center' | 'right';

export interface PageHeaderBackgroundProps {
  /** Page title */
  title: string;

  /** Page title */
  details: string;

  /** Optional description text (max 260 chars recommended for 2 rows) */
  description?: string;

  /** Background image URL */
  backgroundImage?: string;

  /** Background color (fallback if no image) */
  backgroundColor?: string;

  /** Show back navigation button */
  showBackButton?: boolean;

  /** Custom back click handler */
  onBackClick?: () => void;

  /** URL for back link */
  goBackUrl?: string;

  /** Text for back link */
  goBackText?: string;

  /** Text color - defaults to theme variable */
  textColor?: string;

  /** Overlay color - Display the overlay color and set  the specified imageOpacity to the image */
  blurBg?: boolean;

  /** Overlay color - defaults to theme variable */
  overlayColor?: string;

  /** Overlay opacity (0-1) - defaults to 0.30 */
  overlayOpacity?: number;

  /** Image grayscale filter (0-100%) - defaults to 100 */
  grayscale?: number;

  /** Image opacity (0-1) - defaults to 0.40 */
  imageOpacity?: number;

  /** Height configuration per breakpoint */
  height?: {
    xs?: string;
    sm?: string;
    md?: string;
    lg?: string;
    xl?: string;
  };

  /** Horizontal alignment of the content in desktop*/
  alignment?: Alignment;

  /**Horizontal alignment of the content in mobile */
  mobileAlignment?: Alignment;

  /** Additional CSS classes */
  externalWebLink?: string;

  /** Additional CSS classes */
  className?: string;

  /** Optional children rendered below the description */
  children?: React.ReactNode;
}

/**
 * Format a URL path segment to title case
 */
const formatPathToTitle = (path: string): string => {
  return path
    .split('-')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
};

/**
 * Get formatted back link text from referrer URL
 */
const getBackLinkText = (
  referrer: string,
  currentHostname: string,
): string | null => {
  if (!referrer) return null;

  try {
    const referrerUrl = new URL(referrer);

    if (referrerUrl.hostname !== currentHostname) {
      return null;
    }

    const pathname = referrerUrl.pathname;
    const pathSegments = pathname.split('/').filter(Boolean);

    if (pathSegments.length === 0) {
      return 'Go back to Homepage';
    }

    const lastSegment = pathSegments[pathSegments.length - 1];

    if (lastSegment.startsWith('searchableText')) {
      return 'Go back to Search results';
    }
    if (lastSegment.startsWith('contact-info')) {
      return 'Go back to Contact';
    }

    const formattedTitle = formatPathToTitle(lastSegment);
    return `Go back to ${formattedTitle}`;
  } catch (error) {
    return null;
  }
};

const PageHeaderBackground: React.FC<PageHeaderBackgroundProps> = ({
  title,
  details,
  description,
  backgroundImage,
  backgroundColor,
  showBackButton = false,
  onBackClick,
  goBackUrl,
  goBackText,
  textColor,
  blurBg = false,
  overlayColor,
  overlayOpacity = 0.2,
  grayscale = 100,
  imageOpacity = 0.2,
  height,
  alignment = 'left',
  mobileAlignment = 'left',
  externalWebLink,
  className,
  children,
}) => {
  const history = useHistory();
  const [computedBackText, setComputedBackText] = useState<string>('Go back');

  // CSS custom properties for responsive heights
  const containerStyles: React.CSSProperties = {
    '--page-header-bg-text-color': textColor,
    '--page-header-bg-overlay-color-display': blurBg ? 'flex' : 'None',
    '--page-header-bg-overlay-color': overlayColor,
    '--page-header-bg-overlay-opacity': overlayOpacity,
    '--page-header-bg-grayscale': `${grayscale}%`,
    '--page-header-bg-image-opacity': blurBg ? imageOpacity : 1,
    '--page-header-bg-height-xs': height?.xs || "var(--page-header-bg-variantdefault-minheight-xs)",
    '--page-header-bg-height-sm': height?.sm || height?.xs || "var(--page-header-bg-variantdefault-minheight-sm)",
    '--page-header-bg-height-md': height?.md || "var(--page-header-bg-variantdefault-minheight-md)",
    '--page-header-bg-height-lg': height?.lg || height?.md || "var(--page-header-bg-variantdefault-minheight-lg)",
    '--page-header-bg-height-xl': height?.xl || height?.lg || "var(--page-header-bg-variantdefault-minheight-xl)",
  } as React.CSSProperties;

  useEffect(() => {
    if (showBackButton && typeof window !== 'undefined' && !goBackText) {
      const referrer = document.referrer;
      const currentHostname = window.location.hostname;
      const backText = getBackLinkText(referrer, currentHostname);

      if (backText) {
        setComputedBackText(backText);
      } else {
        setComputedBackText('Go back');
      }
    }
  }, [showBackButton, goBackText]);

  const displayBackText = goBackText || computedBackText;

  const containerClasses = cx(
    'page-header-background',
    {
      'page-header-background--with-image': backgroundImage,
    },
    className,
  );

  return (
    <PageSection className={containerClasses} style={containerStyles}>
      <ContentWrapper>
        {/* Background image layer with filters */}
        {backgroundImage && (
            <div
              className="page-header-background__image"
              style={{ backgroundImage: `url(${backgroundImage})` }}
              aria-hidden="true"
            />
        )}

      {/* Color overlay layer */}
      <div className="page-header-background__overlay" aria-hidden="true" />

        {/* Content layer */}
        <ContentWide 
          className="page-header-background__content" 
          alignment = {alignment} 
          mobileAlignment = {mobileAlignment} 
          gap="var(--ess-space-xl)"
          paddingBottom="0" 
          paddingTop="0">
          {showBackButton && (
            <div className="page-header-background__back-link">
              <ESSButton 
                variant="link-icon"
                size="lg"
                onClick={(e) => handleBackClick(e, onBackClick, goBackUrl, history)}
                aria-label={displayBackText}
                classname="page-header-background__back-link">
                <svg
                  width="16"
                  height="16"
                  viewBox="0 0 16 16"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                  className="page-header-background__back-icon"
                  aria-hidden="true"
                >
                  <path
                    d="M10 12L6 8L10 4"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
                {displayBackText}
              </ESSButton>
            </div>
          )}
          <div className="page-header-background__title ess-text">
            <h1>{title}</h1>
            {details && (
              <div className="page-header-background__details">
                {details}
              </div>
            )}
          </div>

          <ContentNarrow 
            className="page-header-background__description" 
            alignment = {alignment}
            mobileAlignment={mobileAlignment}
          >
            {description && (
              <RichText content={description}/>
            )}

            {externalWebLink && 
              <ESSButton onClick={() => {
                if (typeof window !== 'undefined') {
                  window.open(externalWebLink);
                }
              }}>
                Visit website
              </ESSButton>
            }
          </ContentNarrow>
        </ContentWide>
        {children && (
          <ContentWide 
            className="page-header-background__children"
            alignment={alignment}
            mobileAlignment = {mobileAlignment} >
              {children}
          </ContentWide>
        )}
      </ContentWrapper>
    </PageSection>
  );
};

export default PageHeaderBackground;
